import React from 'react';
import { AppBar, Toolbar, Typography, Box, Button, Divider } from '@mui/material';

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useConsultantBranding } from '../../contexts/ConsultantBrandingContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import UserMenu from './UserMenu';
import BreadcrumbsNav from './BreadcrumbsNav';
import ThemeToggle from './ThemeToggle';
import { BillingWarningBanner } from '../Billing';
import Footer from '../Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { userPermissions, isConsultantUser, clientCompanies } = useCompany();
  const { branding } = useConsultantBranding();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminPage = location.pathname.startsWith('/admin');
  
  // Check if user can access admin features
  // - is_super_admin: Centre Of Business staff (full access)
  // - can_manage_templates: Network admins who can manage review templates
  // - can_manage_clients: Network admins who can manage client organisations
  const canAccessAdmin = userPermissions?.is_super_admin || userPermissions?.can_manage_templates || userPermissions?.can_manage_clients;
  
  // Hide Companies button for end users with only one company (they auto-redirect anyway)
  const showCompaniesButton = isConsultantUser || clientCompanies.length !== 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: mode === 'dark' 
            ? 'rgba(13, 13, 13, 0.8)' 
            : 'primary.main',
          backdropFilter: mode === 'dark' ? 'blur(20px)' : 'none',
          borderBottom: mode === 'dark' 
            ? '1px solid rgba(255,255,255,0.1)' 
            : 'none',
          boxShadow: mode === 'dark' 
            ? 'none' 
            : undefined,
        }}
      >
        <Toolbar>
          {/* CHECK SET GO Logo - Landing Page Style */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mr: 4,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="CHECK SET GO"
              sx={{
                width: 40,
                height: 48,
                objectFit: 'contain',
                filter: mode === 'light' ? 'brightness(0) invert(1)' : 'none', // White in light mode
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: '1.1rem',
                  lineHeight: 0.9,
                  color: '#FFFFFF',
                  letterSpacing: '2px',
                }}
              >
                CHECK SET GO
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#E53935',
                  fontWeight: 600,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}
              >
                Capability Platform
              </Typography>
            </Box>
          </Box>
          
          {/* Navigation Links */}
          {user && (
            <Box sx={{ display: 'flex', gap: 2, mr: 4 }}>
              <Button 
                color="inherit" 
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard"
                sx={{ 
                  textTransform: 'none',
                  backgroundColor: location.pathname === '/dashboard' ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  }
                }}
              >
                Dashboard
              </Button>
              {showCompaniesButton && (
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/companies')}
                  data-testid="nav-companies"
                  sx={{ 
                    textTransform: 'none',
                    backgroundColor: location.pathname === '/companies' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                    }
                  }}
                >
                  Companies
                </Button>
              )}
              {/* Only show Admin menu to users with proper permissions */}
              {canAccessAdmin && (
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/admin/companies')}
                  data-testid="nav-admin"
                  sx={{ 
                    textTransform: 'none',
                    backgroundColor: isAdminPage ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                    }
                  }}
                >
                  Admin
                </Button>
              )}
            </Box>
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Consultant Co-Branding - only show logo icon, compact for dark mode */}
          {user && branding && branding.logo_url && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mr: 1,
                }}
              >
                <Box
                  component="img"
                  src={branding.logo_url}
                  alt={branding.company_display_name || 'Consultant'}
                  sx={{
                    height: 48,
                    maxWidth: 160,
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)', // White in both light and dark modes
                  }}
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
              </Box>
              
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.2)',
                  mx: 1,
                  my: 1,
                }} 
              />
            </>
          )}
          
          {/* User Menu */}
          {user && <UserMenu />}
        </Toolbar>
      </AppBar>
      
      {/* Keep breadcrumbs for navigation context */}
      <BreadcrumbsNav />
      
      {/* Billing warning banner */}
      <BillingWarningBanner variant="compact" />
      
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
      
      {/* Footer with legal links - adapt to theme */}
      <Footer variant={mode === 'dark' ? 'dark' : 'light'} showCookieSettings={true} />
    </Box>
  );
};

export default Layout; 